import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BosDurum,
  Buton,
  cn,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Tablo,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  hataDefteri,
  hataGozdenGecir,
  hataGuncelle,
  hataHaritasi,
  hataTekrar,
  hataTipleri,
  konuIlerlemesi,
  HATA_TIPI_ADI,
  HATA_TIPI_SIRASI,
  type HataHaritasi,
  type HataKaydi,
  type HataTipi,
} from "./api";

/**
 * HATALI SORU DEFTERİ — F-MST
 *
 * ================================================================
 * UZMAN TARAFI: KAYDI ÖĞRENCİ AÇAR, AÇIKLAMAYI UZMAN YAZAR
 * ================================================================
 * Şartnamedeki iş bölümü bu: öğrenci yanlış yaptığı soruyu kaydeder
 * (F-MST-01), uzman hata tipini düzeltir ve açıklama ekler
 * (F-MST-03). Panelde EKLEME formu yok — uzmanın öğrenci adına soru
 * uydurması, defterin ne işe yaradığını bozardı.
 *
 * ================================================================
 * "ÇÖZÜLDÜ" İKİ DOĞRU İSTİYOR
 * ================================================================
 * Sunucu tek doğruyu yeterli saymıyor ve yanlış bir tekrar
 * "çözüldü" işaretini SIFIRLIYOR. Ekran bunu sayıyla gösteriyor:
 * "1/2 doğru" yazmak, öğrencinin nerede olduğunu tek bakışta
 * anlatıyor — "çözülmedi" tek başına bunu söylemiyor.
 */

const TIP_TONU: Record<string, "notr" | "bilgi" | "uyari" | "hata"> = {
  KNOWLEDGE_GAP: "hata",
  CARELESS: "uyari",
  TIME: "uyari",
  MISREAD: "bilgi",
  CALCULATION: "bilgi",
};

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR");
}

export function HataDefteri({ ogrenciId, ogrenciAdi }: { ogrenciId: string; ogrenciAdi: string }) {
  const [gorunum, setGorunum] = useState<"defter" | "harita" | "gozden">("defter");

  return (
    <div className="flex flex-col gap-4">
      {/*
        `role="tab"` DEĞİL: ok tuşu gezinmesi, gezinen odak ve `tabpanel`
        bağı olmadan ARIA sekme deseni yarım kalır ve yarım desen hiç
        yazmamaktan kötüdür. Gerekçe: `packages/ui/src/Sekmeler.tsx`.
      */}
      <div role="group" aria-label="Hata defteri görünümü" className="flex flex-wrap gap-2">
        {(
          [
            ["defter", "Defter"],
            ["harita", "Hata haritası"],
            ["gozden", "Sınav öncesi"],
          ] as const
        ).map(([a, e]) => (
          <button
            key={a}
            type="button"
            aria-pressed={gorunum === a}
            onClick={() => setGorunum(a)}
            className={cn(
              "min-h-dokunma rounded-alan px-4 text-sm font-medium",
              "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
              gorunum === a
                ? "bg-marka-600 text-notr-0"
                : "bg-notr-100 text-notr-700 hover:bg-notr-200",
            )}
          >
            {e}
          </button>
        ))}
      </div>

      {gorunum === "defter" && <Defter ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} />}
      {gorunum === "harita" && <Harita ogrenciId={ogrenciId} />}
      {gorunum === "gozden" && <GozdenGecirme ogrenciId={ogrenciId} />}
    </div>
  );
}

// =====================================================================
// DEFTER
// =====================================================================

function Defter({ ogrenciId, ogrenciAdi }: { ogrenciId: string; ogrenciAdi: string }) {
  const qc = useQueryClient();
  const [yalnizcaCozulmemis, setYalnizca] = useState(true);
  const [acik, setAcik] = useState<string | null>(null);

  const tipler = useQuery({ queryKey: ["hata-tipleri"], queryFn: hataTipleri, retry: false });
  const liste = useQuery({
    queryKey: ["hata-defteri", ogrenciId, yalnizcaCozulmemis],
    queryFn: () => hataDefteri(ogrenciId, yalnizcaCozulmemis),
    retry: false,
  });

  /*
    Konu listesi öğrencinin KENDİ müfredatından. Uzman hata tipini
    düzeltirken konuyu da düzeltebilmeli: öğrenci "hangi konu"
    sorusunu boş bırakmış olabilir ve konusuz kayıt hata haritasına
    girmiyor — yani programa yansıyamıyor.
  */
  const konular = useQuery({
    queryKey: ["konu-ilerleme", ogrenciId],
    queryFn: () => konuIlerlemesi(ogrenciId),
    retry: false,
  });

  const guncelle = useMutation({
    mutationFn: (g: { id: string; alan: Record<string, unknown> }) => hataGuncelle(g.id, g.alan),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["hata-defteri", ogrenciId] });
      await qc.invalidateQueries({ queryKey: ["hata-haritasi", ogrenciId] });
    },
  });

  const tekrar = useMutation({
    mutationFn: (g: { id: string; dogruMu: boolean }) => hataTekrar(g.id, g.dogruMu),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["hata-defteri", ogrenciId] });
      await qc.invalidateQueries({ queryKey: ["hata-haritasi", ogrenciId] });
    },
  });

  if (liste.isPending || tipler.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Defter alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const tipAdi = (t: string | null): string => (t === null ? "—" : (tipler.data?.[t] ?? t));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="min-h-dokunma flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="accent-marka-600 size-5"
            checked={yalnizcaCozulmemis}
            onChange={(e) => setYalnizca(e.target.checked)}
          />
          <span className="text-notr-800 text-sm">Yalnızca çözülmemişler</span>
        </label>
        <span className="text-notr-600 text-sm">{liste.data.length} kayıt</span>
      </div>

      {(guncelle.isError || tekrar.isError) && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {(guncelle.error ?? tekrar.error) instanceof ApiHatasi
            ? ((guncelle.error ?? tekrar.error) as ApiHatasi).message
            : "İşlem tamamlanamadı."}
        </p>
      )}

      {liste.data.length === 0 ? (
        <BosDurum
          ikon={yalnizcaCozulmemis ? "tamam" : "bos"}
          baslik={yalnizcaCozulmemis ? "Çözülmemiş soru yok" : "Defter boş"}
          /*
            BOŞ DEFTER "HATA YAPMIYOR" DEMEK DEĞİL: kaydı öğrenci
            açıyor ve hiç kaydetmemiş olabilir. İkisini karıştırmak,
            uzmanı yanlış bir sonuca götürür.
          */
          aciklama={
            yalnizcaCozulmemis
              ? `${ogrenciAdi} çözülmemiş soru bırakmamış. Tümünü görmek için üstteki kutuyu kapatın.`
              : `${ogrenciAdi} henüz hiç yanlış soru kaydetmemiş. Bu "hata yapmıyor" demek değil — defteri öğrenci kendisi dolduruyor.`
          }
        />
      ) : (
        <Tablo
          satirlar={liste.data}
          anahtar={(k) => k.id}
          kartBasligi={0}
          sutunlar={[
            {
              baslik: "Ders / konu",
              mobil: true,
              className: "font-medium",
              hucre: (k) =>
                k.topic?.name
                  ? `${k.lesson?.name ?? "—"} · ${k.topic.name}`
                  : /*
                      KONUSUZ KAYIT ayrıca işaretleniyor: hata
                      haritasına girmiyor, yani programa yansıyamıyor.
                      Uzmanın düzeltmesi gereken tek şey bu.
                    */
                    `${k.lesson?.name ?? "—"} · konu seçilmemiş`,
            },
            {
              baslik: "Hata tipi",
              mobil: true,
              hucre: (k) =>
                k.errorType ? (
                  <Rozet ton={TIP_TONU[k.errorType] ?? "notr"}>{tipAdi(k.errorType)}</Rozet>
                ) : (
                  <Rozet ton="uyari">Belirtilmemiş</Rozet>
                ),
            },
            {
              baslik: "Durum",
              mobil: true,
              hucre: (k) =>
                k.resolvedAt ? (
                  <Rozet ton="onay">Çözüldü</Rozet>
                ) : (
                  /*
                    SAYI GÖSTERİLİYOR: "çözülmedi" tek başına
                    öğrencinin nerede olduğunu söylemiyor. İki doğru
                    gerekiyor; biri varsa bunu görmek gerek.
                  */
                  <span className="text-notr-700 text-sm">
                    {k.lastRetryOk === true ? "1/2 doğru" : `${k.retryCount} deneme`}
                  </span>
                ),
            },
            { baslik: "Tarih", hucre: (k) => gun(k.createdAt) },
            {
              baslik: "İşlem",
              mobil: true,
              hucre: (k) => (
                <div className="flex justify-end">
                  <Buton gorunum="sessiz" onClick={() => setAcik(acik === k.id ? null : k.id)}>
                    {acik === k.id ? "Kapat" : "Aç"}
                    <span className="sr-only"> — {k.topic?.name ?? k.lesson?.name ?? "kayıt"}</span>
                  </Buton>
                </div>
              ),
            },
          ]}
          altSatir={(k) =>
            acik === k.id ? (
              <div className="border-notr-100 bg-notr-50 border-b px-3 py-3">
                <Ayrinti
                  kayit={k}
                  tipler={tipler.data ?? {}}
                  konular={(konular.data ?? []).map((x) => ({
                    id: x.topicId,
                    ad: x.ad,
                    ders: x.ders,
                  }))}
                  onGuncelle={(alan) => guncelle.mutate({ id: k.id, alan })}
                  onTekrar={(dogruMu) => tekrar.mutate({ id: k.id, dogruMu })}
                  islemde={guncelle.isPending || tekrar.isPending}
                />
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
}

function Ayrinti({
  kayit,
  tipler,
  konular,
  onGuncelle,
  onTekrar,
  islemde,
}: {
  kayit: HataKaydi;
  tipler: Record<string, string>;
  konular: Array<{ id: string; ad: string; ders: string }>;
  onGuncelle: (alan: Record<string, unknown>) => void;
  onTekrar: (dogruMu: boolean) => void;
  islemde: boolean;
}) {
  const [not, setNot] = useState(kayit.coachNote ?? "");

  return (
    <div className="flex flex-col gap-3">
      {kayit.studentNote && (
        <div>
          <p className="text-notr-600 text-xs font-medium">Öğrencinin notu</p>
          <p className="text-notr-800 text-sm whitespace-pre-wrap">{kayit.studentNote}</p>
        </div>
      )}

      {/* tarama-1024 D1 — eski kayitta `javascript:`/`data:` kalmis olabilir; yalniz http/https baglanti cizilir. */}
      {kayit.imageUrl && /^https?:\/\//i.test(kayit.imageUrl) && (
        <a
          href={kayit.imageUrl}
          target="_blank"
          rel="noreferrer"
          className="text-marka-600 focus-visible:outline-marka-600 text-sm underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Soru görselini aç
        </a>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Secim
          etiket="Hata tipi"
          value={kayit.errorType ?? ""}
          disabled={islemde}
          onChange={(e) => onGuncelle({ errorType: (e.target.value || null) as HataTipi | null })}
        >
          <option value="">Belirtilmemiş</option>
          {Object.entries(tipler).map(([k, a]) => (
            <option key={k} value={k}>
              {a}
            </option>
          ))}
        </Secim>

        <Secim
          etiket="Konu"
          value={kayit.topicId ?? ""}
          disabled={islemde}
          onChange={(e) => onGuncelle({ topicId: e.target.value || null })}
        >
          {/* Konusuz kayıt hata haritasına GİRMİYOR — düzeltilmesi gereken tek alan bu. */}
          <option value="">Konu seçilmemiş</option>
          {konular.map((k) => (
            <option key={k.id} value={k.id}>
              {k.ders} · {k.ad}
            </option>
          ))}
        </Secim>
      </div>

      <MetinAlani
        etiket="Uzman açıklaması"
        rows={3}
        value={not}
        onChange={(e) => setNot(e.target.value)}
        yardim="Öğrenci bunu görecek. “Tekrar bak” demek işe yaramaz; neyi yanlış anladığını yaz."
      />
      <div className="flex flex-wrap gap-2">
        <Buton
          gorunum="ikincil"
          yukleniyor={islemde}
          disabled={not.trim() === (kayit.coachNote ?? "")}
          onClick={() => onGuncelle({ coachNote: not.trim() || null })}
        >
          Açıklamayı kaydet
        </Buton>

        {/*
          TEKRAR SONUCU UZMANDAN DA GİRİLEBİLİYOR: soruyu birlikte
          çözdüklerinde öğrencinin ayrıca uygulamaya girmesini
          beklemek, kaydın hiç düşmemesine yol açardı.
        */}
        {!kayit.resolvedAt && (
          <>
            <Buton gorunum="ikincil" yukleniyor={islemde} onClick={() => onTekrar(true)}>
              Tekrar çözdü — doğru
            </Buton>
            <Buton gorunum="sessiz" yukleniyor={islemde} onClick={() => onTekrar(false)}>
              Tekrar çözdü — yanlış
            </Buton>
          </>
        )}
      </div>
      {!kayit.resolvedAt && (
        <p className="text-notr-600 text-xs">
          “Çözüldü” işareti için İKİ kez üst üste doğru gerekir. Yanlış bir tekrar sayacı sıfırlar —
          artık doğru olmayan bir bilgi öğrenciyi yanıltır.
        </p>
      )}
    </div>
  );
}

// =====================================================================
// HATA HARİTASI — F-MST-05
// =====================================================================

/**
 * ISI HARİTASI — F-MST-07
 *
 * ================================================================
 * "BASKIN HATA" TEK BAŞINA YANILTICIYDI
 * ================================================================
 * Tabloda konu başına tek bir rozet vardı. 12 dikkatsizlik + 11 bilgi
 * eksiği ile 12 dikkatsizlik + 1 bilgi eksiği aynı görünüyordu — oysa
 * ilkine konu anlatımı, ikincisine soru çözümü yazılır. Beraberlikte
 * `baskinTip` `null` dönüyor ve o satırda hiçbir bilgi kalmıyordu.
 *
 * ================================================================
 * NEDEN ISI HARİTASI, NEDEN ÇUBUK GRAFİK DEĞİL
 * ================================================================
 * Çizimin kendi gerekçesi (`HataHaritasi.dc.html`): yığılmış çubuk
 * grafik 10 konuda güzel, 200'de okunamaz. Isı haritası satır
 * ekleyerek uzuyor ve göz koyu hücreleri tarayarak buluyor —
 * "Türev + dikkatsizlik" örüntüsü tek bakışta çıkıyor.
 *
 * ================================================================
 * RENK TEK BAŞINA ANLAM TAŞIMIYOR
 * ================================================================
 * Sayı her hücrede YAZILI. Çizim de bunu açıkça söylüyor ve depo
 * kuralı zaten bu: renk körü bir kullanıcı için koyuluk farkı
 * okunabilir bilgi değil.
 *
 * SÜTUN SIRASI SÖZLEŞMEDEN (`HATA_TIPI_SIRASI`). Burada yeniden
 * yazsaydık iki öğrencinin haritası farklı sıralı çizilebilirdi —
 * hiçbir şey patlamaz, yalnızca uzmanın çıkardığı sonuç yanlış olurdu.
 */
function IsiHaritasi({ konular }: { konular: HataHaritasi["konular"] }) {
  /*
    KOYULUK SATIR İÇİNDE DEĞİL, HARİTA GENELİNDE ölçekleniyor.
    Satır içinde ölçeklenseydi tek yanlışı olan konunun hücresi de
    en koyu çıkar ve göz onu "sorunlu" sanırdı.
  */
  const enYuksek = Math.max(
    1,
    ...konular.flatMap((k) => HATA_TIPI_SIRASI.map((t) => k.tipDagilimi[t] ?? 0)),
  );

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-notr-900 text-sm font-semibold">Hata tipi dağılımı</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">Konu ve hata tipine göre yanlış sayısı ısı haritası</caption>
          <thead>
            <tr className="text-notr-600 text-left text-xs">
              <th scope="col" className="p-2 font-medium">
                Konu
              </th>
              {HATA_TIPI_SIRASI.map((t) => (
                <th key={t} scope="col" className="p-2 text-center font-medium">
                  {HATA_TIPI_ADI[t]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {konular.map((k) => (
              <tr key={k.topicId}>
                <th scope="row" className="text-notr-900 p-2 text-left font-medium">
                  {k.konuAdi}
                </th>
                {HATA_TIPI_SIRASI.map((t) => {
                  const n = k.tipDagilimi[t] ?? 0;
                  return (
                    <td key={t} className="p-1">
                      <span
                        className={cn(
                          "rounded-alan flex min-h-[34px] items-center justify-center tabular-nums",
                          n === 0 ? "text-notr-400" : "text-notr-900 font-medium",
                        )}
                        /*
                          Koyuluk satır içi stille veriliyor çünkü oran
                          veriye bağlı; Tailwind sınıfı üretilemez.
                          `uyari` tonu marka renginden AYRI: harita bir
                          eylem değil, bir ölçüm.
                        */
                        style={
                          n === 0
                            ? undefined
                            : {
                                backgroundColor: `rgb(217 119 6 / ${0.08 + (n / enYuksek) * 0.32})`,
                              }
                        }
                      >
                        {n}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-notr-600 text-xs">
        {/*
          TOPLAMLARIN TUTMAMASI BİR HATA DEĞİL — söylenmezse uzman
          sayıları toplayıp "eksik" sanır. Sunucunun yorumu da bunu
          açıkça yazıyor.
        */}
        Tipi işaretlenmemiş kayıtlar hiçbir sütuna sayılmıyor; bu yüzden satır toplamı “Toplam”
        sütunundan küçük olabilir.
      </p>
    </div>
  );
}

function Harita({ ogrenciId }: { ogrenciId: string }) {
  const harita = useQuery({
    queryKey: ["hata-haritasi", ogrenciId],
    queryFn: () => hataHaritasi(ogrenciId),
    retry: false,
  });
  const tipler = useQuery({ queryKey: ["hata-tipleri"], queryFn: hataTipleri, retry: false });

  if (harita.isPending) return <Iskelet satir={3} />;
  if (harita.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Harita alınamadı"
        aciklama={harita.error instanceof ApiHatasi ? harita.error.message : undefined}
      />
    );
  }

  return (
    <Kart
      baslik="Konu bazlı hata haritası"
      aciklama="ÇÖZÜLMEMİŞ sayısına göre sıralı. Toplama göre sıralasaydık öğrencinin çoktan hallettiği konu başta kalır ve ona tekrar çalışma yazılırdı."
    >
      <div className="flex flex-col gap-3">
        {harita.data.konusuzKayit > 0 && (
          /*
            KONUSUZ KAYIT SAYISI ÖNDE: bu kayıtlar haritada YOK, yani
            programa yansıyamıyorlar. Söylenmezse uzman haritayı tam
            sanır ve eksik bir listeye göre program yazar.
          */
          <p className="rounded-kart shadow-kart border-uyari-500 bg-uyari-50 text-notr-800 border px-3 py-2 text-sm">
            <strong>{harita.data.konusuzKayit} kayıtta konu seçilmemiş</strong> ve bu kayıtlar
            haritada görünmüyor — yani programa yansıyamazlar. “Defter” sekmesinden konularını
            işaretleyin.
          </p>
        )}

        {harita.data.konular.length === 0 ? (
          <BosDurum
            ikon="bos"
            baslik="Haritada konu yok"
            aciklama="Konusu işaretlenmiş hata kaydı bulunmuyor."
          />
        ) : (
          <>
            <Tablo
              satirlar={harita.data.konular}
              anahtar={(k) => k.topicId}
              kartBasligi={0}
              sutunlar={[
                { baslik: "Konu", mobil: true, className: "font-medium", hucre: (k) => k.konuAdi },
                { baslik: "Ders", mobil: true, hucre: (k) => k.dersAdi ?? "—" },
                { baslik: "Toplam", sayisal: true, hucre: (k) => k.toplam },
                {
                  baslik: "Çözülmemiş",
                  mobil: true,
                  sayisal: true,
                  hucre: (k) => (
                    <span className={k.cozulmemis > 0 ? "text-uyari-600 font-medium" : ""}>
                      {k.cozulmemis}
                    </span>
                  ),
                },
                {
                  baslik: "Baskın hata",
                  hucre: (k) =>
                    k.baskinTip ? (
                      <Rozet ton={TIP_TONU[k.baskinTip] ?? "notr"}>
                        {tipler.data?.[k.baskinTip] ?? k.baskinTip}
                      </Rozet>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />

            <IsiHaritasi konular={harita.data.konular} />
          </>
        )}
      </div>
    </Kart>
  );
}

// =====================================================================
// SINAV ÖNCESİ GÖZDEN GEÇİRME — F-MST-06
// =====================================================================

function GozdenGecirme({ ogrenciId }: { ogrenciId: string }) {
  const liste = useQuery({
    queryKey: ["hata-gozden", ogrenciId],
    queryFn: () => hataGozdenGecir(ogrenciId, 20),
    retry: false,
  });
  const tipler = useQuery({ queryKey: ["hata-tipleri"], queryFn: hataTipleri, retry: false });

  if (liste.isPending) return <Iskelet satir={3} />;
  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Liste alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
      />
    );
  }

  if (liste.data.length === 0) {
    return (
      <BosDurum
        ikon="tamam"
        baslik="Gözden geçirilecek soru yok"
        aciklama="Çözülmemiş hata kaydı bulunmuyor."
      />
    );
  }

  return (
    <Kart
      baslik="Sınav öncesi gözden geçirme"
      aciklama="Çözülmemişler, EN ESKİ hata önce — en uzun süredir dokunulmamış olan, unutulmuş olma ihtimali en yüksek olandır."
    >
      <ol className="flex flex-col gap-3">
        {liste.data.map((k, i) => (
          <li key={k.id} className="border-notr-100 border-b pb-3 last:border-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-notr-600 text-sm">{i + 1}.</span>
              <span className="text-notr-900 text-sm font-medium">
                {k.lesson?.name ?? "—"}
                {k.topic?.name ? ` · ${k.topic.name}` : ""}
              </span>
              {k.errorType && (
                <Rozet ton={TIP_TONU[k.errorType] ?? "notr"}>
                  {tipler.data?.[k.errorType] ?? k.errorType}
                </Rozet>
              )}
              <span className="text-notr-600 text-xs">{gun(k.createdAt)}</span>
            </div>
            {k.coachNote && (
              <p className="text-notr-700 mt-1 text-sm whitespace-pre-wrap">{k.coachNote}</p>
            )}
          </li>
        ))}
      </ol>
    </Kart>
  );
}
